import { useEffect, useRef, useState } from 'react';
import {
  IModalContent,
  INlpRequest,
  IQuestionOption,
  MessageType,
  Modals,
  TNullable,
} from '@common/types';
import { AgreementBanner } from '@components/agreement-banner';
import { GenericModal } from '@components/generic-modal';
import useChat from '@hooks/use-chat';
import { useTranslation } from 'react-i18next';
import ChoiceResponse from './choice-response';
import CompleteResponse from './complete-response';
import InvalidQueryResponse from './invalid-query-response';
import LoadingMessage from './loading-message';
import MedicalInfoContent from './medical-info-content';
import NoAnswerResponse from './no-answer-response';
import SimpleResponse from './simple-response';
import UserMessage from './user-message';

interface IChatStreamProps {
  request: TNullable<INlpRequest>;
  onShowMoreAnswers: (messageId: number) => void;
  onSelectChoice: (choice: IQuestionOption | undefined) => void;
  isLoading: boolean;
}

function ChatStream(props: IChatStreamProps) {
  const { t } = useTranslation();
  const { messages, selectedTopic } = useChat();
  const [modalContent, setModalContent] = useState<IModalContent>();
  const chatStreamContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatStreamContainerRef?.current?.scroll({
      top: chatStreamContainerRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [props.isLoading, chatStreamContainerRef, messages]);

  function handleModalShow(type: Modals) {
    if (Modals.MedicalInfo === type) {
      const text = <MedicalInfoContent />;
      const title = t('chat.medical_info.title');
      setModalContent({ text, title });
      return;
    }
    setModalContent(undefined);
  }

  return (
    <div
      className="w-full sm:w-[calc(100%-20px)] mt-4 overflow-y-auto max-h-[calc(100vh-220px)] sm:max-h-[calc(100vh-200px)] bg-xircle-main custom-scroll"
      ref={chatStreamContainerRef}
    >
      {props.request && selectedTopic && <AgreementBanner />}
      <div className="flex flex-col justify-start">
        {messages.map((message) => {
          if (message.type === MessageType.UserEntry) {
            return <UserMessage message={message.message} key={message.id} />;
          }
          if (message.type === MessageType.Message || message.type === MessageType.Error) {
            return <SimpleResponse message={message} key={message.id} />;
          }
          if (message.type === MessageType.Response) {
            return (
              <CompleteResponse
                key={message.id}
                message={message}
                onShowMoreAnswers={props.onShowMoreAnswers}
                showModal={handleModalShow}
              />
            );
          }
          if (message.type === MessageType.ChoiceResponse) {
            return (
              <ChoiceResponse
                key={message.id}
                message={message}
                onSelectChoice={props.onSelectChoice}
              />
            );
          }
          if (
            message.type === MessageType.NoAnswerResponse ||
            message.type === MessageType.ForceMedInfo
          ) {
            return <NoAnswerResponse key={message.id} />;
          }
          if (message.type === MessageType.InvalidQuery) {
            return <InvalidQueryResponse key={message.id} />;
          }
        })}
        {modalContent && (
          <GenericModal
            open={!!modalContent}
            onOpenChange={() => setModalContent(undefined)}
            title={modalContent.title}
            description={modalContent.text}
          />
        )}
        {props.isLoading && <LoadingMessage />}
      </div>
    </div>
  );
}

export default ChatStream;
